import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Button, Menu, MenuItem } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import publicService from '../../services/publicService';
import mainLogo from 'assets/images/logo_verde.svg';
import { Outlet } from 'react-router-dom';
import factoryService from '../../services/admin/factoryService';
import { useAlert } from '../../context/AlertContext';

const PublicBoardHeader = () => {
    const theme = useTheme();
    const { showAlert } = useAlert();

    const [now, setNow] = useState(dayjs());
    const [headerInfo, setHeaderInfo] = useState({
        factoryName: '—',
        shiftName: '—'
    });
    const [selectedFactory, setSelectedFactory] = useState(null);
    const [factories, setFactories] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };
    //const { factoryCode } = useParams();

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(dayjs());
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    function loadCatalogs() {
        publicService
            .getEnabledFactories()
            .then((res) => setFactories(res ?? []))
            .catch(() => showAlert('Error al cargar plantas', 'error'));
    }

    useEffect(() => {
        loadCatalogs();
    }, []);

    useEffect(() => {
        if (!factories.length) return;

        const storedCode = localStorage.getItem('selectedFactoryCode');

        if (storedCode) {
            const factory = factories.find((f) => f.code === storedCode);

            if (factory) {
                setSelectedFactory(factory);
                return;
            }
        }

        // Planta por defecto
        setSelectedFactory(factories[0]);
    }, [factories]);

    useEffect(() => {
        if (!selectedFactory?.code) return;

        publicService
            .getHeaderInfo(selectedFactory.code)
            .then((data) => {
                setHeaderInfo({
                    factoryName: data.factoryName,
                    shiftName: data.shiftName
                });
            })
            .catch((err) => {
                console.error('[PublicBoardHeader] Error:', err);
            });
    }, [selectedFactory]);

    return (
        <>
            <Box
                component="header"
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: '10%',
                    backgroundColor: theme.palette.secondary.main800,
                    py: 1,
                    px: 2,
                    alignItems: 'center'
                }}
            >
                {/* IZQUIERDA: LOGO + SISTEMA */}

                <Box
                    sx={{
                        flex: '0 0 30%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Box component="img" src={mainLogo} alt="logo" sx={{ height: '5vh' }} />

                    <Stack>
                        <Typography variant="h5" color={theme.palette.primary.dark}>
                            HR x HR Digital Boards
                        </Typography>
                        <Typography variant="h6" color={theme.palette.primary.dark}>
                            Sistema de producción
                        </Typography>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        flex: '0 0 20%',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Stack alignItems="center" spacing={0}>
                        <Typography variant="h6" color={theme.palette.primary.dark}>
                            PLANTA
                        </Typography>

                        <Button
                            onClick={handleOpenMenu}
                            endIcon={<KeyboardArrowDownIcon />}
                            sx={{
                                p: 0,
                                minWidth: 'unset',
                                textTransform: 'none',
                                color: theme.palette.primary.dark,

                                '& .MuiButton-endIcon': {
                                    ml: 0.5
                                }
                            }}
                        >
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {selectedFactory?.name || 'Seleccionar'}
                            </Typography>
                        </Button>

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseMenu}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}
                        >
                            {factories.map((factory) => (
                                <MenuItem
                                    key={factory.code}
                                    onClick={() => {
                                        setSelectedFactory(factory);
                                        localStorage.setItem('selectedFactoryCode', factory.code);
                                        handleCloseMenu();
                                    }}
                                >
                                    {factory.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Stack>
                </Box>

                {/* 3️⃣ TURNO (centrado visualmente) */}
                <Box
                    sx={{
                        flex: '0 0 20%',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Stack alignItems="center" spacing={0}>
                        <Typography variant="h5" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} color={theme.palette.primary.dark}>
                            TURNO
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} color={theme.palette.primary.dark}>
                            {headerInfo.shiftName}
                        </Typography>
                    </Stack>
                </Box>

                {/* 4️⃣ HORA / FECHA (derecha) */}
                <Box
                    sx={{
                        flex: '0 0 30%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 3
                    }}
                >
                    <Stack direction="row" alignItems="center" gap={1}>
                        <AccessTimeIcon sx={{ color: theme.palette.primary.dark }} />
                        <Typography color={theme.palette.primary.dark} variant="h5">
                            {now.format('hh:mm A')}
                        </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" gap={1}>
                        <CalendarMonthIcon sx={{ color: theme.palette.primary.dark }} />
                        <Typography color={theme.palette.primary.dark} variant="h5">
                            {now.locale('es').format('dddd, D [de] MMMM [de] YYYY')}
                        </Typography>
                    </Stack>
                </Box>
            </Box>
            <Outlet
                context={{
                    selectedFactory,
                    setSelectedFactory
                }}
            />
        </>
    );
};

export default PublicBoardHeader;
